import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react'
import {Box, Chip, InputBase, MenuItem, Paper, Popper, Select, TextareaAutosize, Tooltip, Typography} from "@mui/material"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined"
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined"
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined"
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined"
import {
    DataGrid,
    GridCellModes,
    GridCellModesModel,
    GridCellParams,
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridSortModel,
    useGridApiContext,
} from "@mui/x-data-grid"
import RowJson from "../../api/json/RowJson"
import {RowRequests, RowSearchParams, RowSortProperty, SortDirection} from "../../api/requests/RowRequests"
import {ProjectJson} from "../../api/json/ProjectJson"
import {AnyColumnJson, ColumnPredefinedOptionsJson} from "../../api/json/ColumnJson"
import CellJson from "../../api/json/CellJson"
import {ColumnType} from "../../api/enum/ColumnType"
import {RowDataset, resolveDatasetName} from "../../api/enum/RowDataset"
import {RowOrigin, resolveRowOriginName} from "../../api/enum/RowOrigin"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import RowActionsMenu from "./RowActionsMenu"
import {TimeUtil} from "../../library/TimeUtil"
import {loadColumnWidths, saveColumnWidth} from "../../library/ColumnWidthStorage"

interface RowTableProps {
    project: ProjectJson
    searchParams: RowSearchParams
    reload: number
    onRowsChange?: (rows: RowJson[]) => void
    onSortChange: (sort: RowSearchParams['sort']) => void
}

const DEFAULT_PAGE_SIZE = 50
const PAGE_SIZE_OPTIONS = [25, 50, 100]

const DATASET_COLORS: Record<RowDataset, string> = {
    [RowDataset.TRAINING]: '#ccc',
    [RowDataset.VALIDATION]: '#FFCB87',
}

function SourceIcon({origin}: { origin: RowOrigin | undefined }) {
    if (!origin) {
        return <></>
    }
    const iconProps = {fontSize: 'small' as const, sx: {color: 'text.secondary', opacity: 0.8}}
    let icon: React.ReactNode
    switch (origin) {
        case RowOrigin.IMPORT:
            icon = <FileUploadOutlinedIcon {...iconProps}/>
            break
        case RowOrigin.MANUAL:
            icon = <EditOutlinedIcon {...iconProps}/>
            break
        case RowOrigin.SYNTHESIS:
            icon = <PlaylistAddOutlinedIcon {...iconProps}/>
            break
        case RowOrigin.PLAYGROUND:
            icon = <BoltOutlinedIcon {...iconProps}/>
            break
        case RowOrigin.COPY:
            icon = <ContentCopyOutlinedIcon {...iconProps}/>
            break
        default:
            icon = <HelpOutlineOutlinedIcon {...iconProps}/>
    }
    return (
        <Tooltip title={resolveRowOriginName(origin)}>
            <Box sx={{display: 'inline-flex', alignItems: 'center'}}>{icon}</Box>
        </Tooltip>
    )
}

function DatasetChip({dataset}: { dataset: RowDataset }) {
    const color = DATASET_COLORS[dataset]
    return (
        <Chip
            size="small"
            variant="outlined"
            label={resolveDatasetName(dataset)}
            sx={{
                borderColor: color,
                backgroundColor: `${color}33`,
                color: 'text.primary',
                cursor: 'pointer',
            }}
        />
    )
}

function DatasetEditCell({params}: { params: GridRenderEditCellParams }) {
    const apiRef = useGridApiContext()
    const value = (params.value as RowDataset) ?? RowDataset.TRAINING

    return (
        <Select
            value={value}
            onChange={async (e) => {
                await apiRef.current.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: e.target.value as RowDataset,
                })
                apiRef.current.stopCellEditMode({id: params.id, field: params.field})
            }}
            onClose={() => {
                apiRef.current.stopCellEditMode({
                    id: params.id,
                    field: params.field,
                    ignoreModifications: true,
                })
            }}
            autoFocus
            defaultOpen
            fullWidth
            size="small"
            variant="standard"
            disableUnderline
            sx={{px: 1, fontSize: 14}}
        >
            <MenuItem value={RowDataset.TRAINING}>{resolveDatasetName(RowDataset.TRAINING)}</MenuItem>
            <MenuItem value={RowDataset.VALIDATION}>{resolveDatasetName(RowDataset.VALIDATION)}</MenuItem>
        </Select>
    )
}

function MultilineEditCell({params}: { params: GridRenderEditCellParams }) {
    const apiRef = useGridApiContext()
    const value: string = (params.value as string) ?? ""
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)

    // Look up the cell DOM node *after* the edit cell mounts so the Popper has something to anchor to.
    useLayoutEffect(() => {
        setAnchor(apiRef.current.getCellElement(params.id, params.field))
    }, [apiRef, params.id, params.field])

    const setValue = (newValue: string) => {
        apiRef.current.setEditCellValue({id: params.id, field: params.field, value: newValue})
    }

    const stop = (ignore = false) => {
        apiRef.current.stopCellEditMode({
            id: params.id,
            field: params.field,
            ignoreModifications: ignore,
        })
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            stop(true)
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            stop(false)
        }
        // Plain Enter / Shift+Enter inserts a newline (default textarea behavior)
    }

    const cellWidth = anchor?.offsetWidth ?? 240
    const cellHeight = anchor?.offsetHeight ?? 52

    return (
        <>
            {/* Keep an empty placeholder cell so DataGrid still measures the cell — the real editor lives in the Popper. */}
            <Box sx={{width: '100%', height: '100%'}}/>
            <Popper
                open={Boolean(anchor)}
                anchorEl={anchor}
                placement="bottom-start"
                style={{zIndex: 1300}}
                modifiers={[{name: 'offset', options: {offset: [0, -cellHeight]}}]}
            >
                <Paper elevation={4} sx={{width: cellWidth, minWidth: 240}}>
                    <TextareaAutosize
                        autoFocus
                        value={value}
                        minRows={Math.max(1, Math.floor(cellHeight / 20))}
                        maxRows={16}
                        onChange={(e) => setValue(e.target.value)}
                        onBlur={() => stop(false)}
                        onKeyDown={handleKeyDown}
                        style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '8px 10px',
                            fontFamily: 'inherit',
                            fontSize: 14,
                            lineHeight: 1.43,
                            background: 'transparent',
                        }}
                    />
                </Paper>
            </Popper>
        </>
    )
}

function CellEditCell({column, params}: { column: AnyColumnJson, params: GridRenderEditCellParams }) {
    const apiRef = useGridApiContext()
    const value: string = (params.value as string) ?? ""

    const setValue = (newValue: string) => {
        apiRef.current.setEditCellValue({id: params.id, field: params.field, value: newValue})
    }

    const handleBlur = () => {
        if (column.type === ColumnType.NUMBER) {
            let n = parseFloat(value)
            if (isNaN(n)) n = 0
            setValue(n.toString())
        } else if (column.type === ColumnType.URL && value !== "") {
            if (!value.startsWith("https://") && !value.startsWith("http://")) {
                setValue(`https://${value}`)
            }
        }
    }

    if (column.type === ColumnType.TEXT && value.includes('\n')) {
        return <MultilineEditCell params={params}/>
    }

    if (column.type === ColumnType.PREDEFINED_OPTIONS) {
        const options = (column as ColumnPredefinedOptionsJson).predefinedOptions ?? []
        return (
            <Select
                value={value}
                onChange={async (e) => {
                    await apiRef.current.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: e.target.value as string,
                    })
                    apiRef.current.stopCellEditMode({id: params.id, field: params.field})
                }}
                onClose={() => {
                    apiRef.current.stopCellEditMode({
                        id: params.id,
                        field: params.field,
                        ignoreModifications: true,
                    })
                }}
                autoFocus
                defaultOpen
                fullWidth
                size="small"
                variant="standard"
                disableUnderline
                sx={{px: 1, fontSize: 14}}
            >
                {options.map(o => (
                    <MenuItem key={o.id} value={o.value}>{o.value}</MenuItem>
                ))}
            </Select>
        )
    }

    const inputMode = column.type === ColumnType.NUMBER ? 'numeric' : 'text'

    return (
        <InputBase
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
            autoFocus
            fullWidth
            inputProps={{inputMode}}
            sx={{
                height: '100%',
                width: '100%',
                px: 1,
                fontSize: 14,
                '& input': {height: '100%', p: 0},
            }}
        />
    )
}

function RowTable({project, searchParams, reload, onRowsChange, onSortChange}: RowTableProps) {
    const [rows, setRows] = useState<RowJson[]>([])
    const [rowCount, setRowCount] = useState<number>(0)
    const [page, setPage] = useState<number>(0)
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<ApiError>()
    const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({})

    // Notify parent on row changes so the ActiveTasksStrip's page-saturation gate can read them.
    useEffect(() => {
        if (onRowsChange) onRowsChange(rows)
    }, [rows, onRowsChange])

    // Reset to first page whenever the search params or external reload change
    useEffect(() => {
        setPage(0)
    }, [
        project.id,
        searchParams.dataset,
        searchParams.search?.value,
        searchParams.search?.fieldId,
        searchParams.sort.by,
        searchParams.sort.direction,
        reload,
    ])

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        RowRequests.list(project.id, searchParams, page, pageSize)
            .then(response => {
                if (cancelled) return
                setRows(response.content)
                setRowCount(response.totalElements)
                setError(undefined)
            })
            .catch(e => {
                if (cancelled) return
                setError(e)
            })
            .finally(() => {
                if (cancelled) return
                setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [
        project.id,
        searchParams.dataset,
        searchParams.search?.value,
        searchParams.search?.fieldId,
        searchParams.sort.by,
        searchParams.sort.direction,
        reload,
        page,
        pageSize,
    ])

    const columns: GridColDef<RowJson>[] = useMemo(() => {
        const updatedColumn: GridColDef<RowJson> = {
            field: '__updated__',
            headerName: 'Updated',
            width: 120,
            sortable: true,
            filterable: false,
            editable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: RowJson) => row.updated,
            renderCell: (params: GridRenderCellParams<RowJson, string>) => (
                <Typography variant="body2" color="text.secondary">
                    {TimeUtil.ago(params.value ?? "")}
                </Typography>
            ),
        }

        const datasetColumn: GridColDef<RowJson> = {
            field: '__dataset__',
            headerName: 'Dataset',
            width: 130,
            editable: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: RowJson) => row.dataset,
            valueSetter: (value: RowDataset, row: RowJson) => ({
                ...row,
                dataset: value ?? row.dataset,
            }),
            renderCell: (params: GridRenderCellParams<RowJson, RowDataset>) => (
                <DatasetChip dataset={params.value ?? RowDataset.TRAINING}/>
            ),
            renderEditCell: (params) => <DatasetEditCell params={params}/>,
        }

        const sourceColumn: GridColDef<RowJson> = {
            field: '__source__',
            headerName: 'Source',
            width: 80,
            sortable: false,
            filterable: false,
            editable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (_value, row: RowJson) => row.origin,
            renderCell: (params: GridRenderCellParams<RowJson, RowOrigin>) => (
                <SourceIcon origin={params.value}/>
            ),
        }

        const sortedFields = [...project.columns].sort((a, b) => a.order - b.order)
        const fieldColumns = sortedFields.map<GridColDef<RowJson>>(column => ({
            field: column.reference,
            headerName: column.name,
            flex: 1,
            minWidth: 160,
            editable: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            valueGetter: (_value, row: RowJson) =>
                row.cells.find(v => v.column.reference === column.reference)?.value ?? "",
            valueSetter: (value: string, row: RowJson) => {
                const next: RowJson = {
                    ...row,
                    cells: [...row.cells],
                }
                const idx = next.cells.findIndex(v => v.column.reference === column.reference)
                const updatedValue: CellJson = {
                    value: value ?? "",
                    column: {
                        id: column.id,
                        name: column.name,
                        reference: column.reference,
                        type: column.type,
                        order: column.order,
                        helperText: column.helperText,
                    },
                }
                if (idx >= 0) {
                    next.cells[idx] = updatedValue
                } else {
                    next.cells.push(updatedValue)
                }
                return next
            },
            renderEditCell: (params) => <CellEditCell column={column} params={params}/>,
        }))

        const actionsColumn: GridColDef<RowJson> = {
            field: '__actions__',
            headerName: '',
            width: 56,
            sortable: false,
            filterable: false,
            editable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<RowJson>) => (
                <RowActionsMenu
                    row={params.row}
                    size="small"
                    didDelete={() => {
                        setRows(rs => rs.filter(r => r.id !== params.row.id))
                        setRowCount(c => Math.max(0, c - 1))
                    }}
                />
            ),
        }

        // Seed any widths the user previously resized; a saved width takes over
        // from `flex` so the column keeps its fixed size across refreshes.
        const savedWidths = loadColumnWidths(project.id)
        const applySavedWidth = (col: GridColDef<RowJson>): GridColDef<RowJson> => {
            const saved = savedWidths[col.field]
            if (saved == null) return col
            const {flex, ...rest} = col
            return {...rest, width: saved}
        }

        return [sourceColumn, updatedColumn, datasetColumn, ...fieldColumns, actionsColumn]
            .map(applySavedWidth)
    }, [project.columns, project.id])

    const handleCellClick = useCallback((params: GridCellParams) => {
        if (!params.isEditable) return
        if (params.cellMode === 'edit') return
        setCellModesModel(prev => ({
            ...prev,
            [params.id]: {
                ...(prev[params.id] ?? {}),
                [params.field]: {mode: GridCellModes.Edit},
            },
        }))
    }, [])

    const processRowUpdate = useCallback(async (newRow: RowJson, oldRow: RowJson) => {
        try {
            const updated = await RowRequests.update(newRow.id, newRow.dataset, newRow.cells)
            setRows(rs => rs.map(r => r.id === updated.id ? updated : r))
            setError(undefined)
            return updated
        } catch (e) {
            setError(e as ApiError)
            return oldRow
        }
    }, [])

    return (
        <>
            {error && <ErrorMessage error={error} sx={{mb: 1}}/>}
            <Box sx={{height: 'calc(100vh - 240px)', width: '100%'}}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    rowCount={rowCount}
                    loading={loading}
                    paginationMode="server"
                    paginationModel={{page, pageSize}}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPaginationModelChange={(model) => {
                        if (model.pageSize !== pageSize) {
                            setPage(0)
                            setPageSize(model.pageSize)
                        } else {
                            setPage(model.page)
                        }
                    }}
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    sortModel={[{
                        field: '__updated__',
                        sort: searchParams.sort.direction === SortDirection.ASC ? 'asc' : 'desc',
                    }]}
                    onSortModelChange={(model: GridSortModel) => {
                        // sortingOrder excludes the "unsorted" state, so model is always non-empty.
                        if (model.length === 0) return
                        onSortChange({
                            by: RowSortProperty.UPDATED,
                            direction: model[0].sort === 'asc' ? SortDirection.ASC : SortDirection.DESC,
                        })
                    }}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    onColumnWidthChange={(params) =>
                        saveColumnWidth(project.id, params.colDef.field, params.width)
                    }
                    onCellClick={handleCellClick}
                    cellModesModel={cellModesModel}
                    onCellModesModelChange={setCellModesModel}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(e) => setError(e as ApiError)}
                    sx={{
                        backgroundColor: 'white',
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                    }}
                />
            </Box>
        </>
    )
}

export default RowTable
